/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

import * as go from 'gojs';

export const colors = {
  text: {
    subtle: '#C1C9CF',
    'form-input-disabled': '#8C9EA1',
    'body-primary': '#FFFFFF',
  },
  layout: {
    'line-secondary': '#40565A',
  },
  notification: {
    'error-dark': '#F26C74',
    'error-dark-80': 'rgba(242, 108, 116, 0.8)',
  },
};

// Node style configurations by category
export const NODE_STYLES = {
  start: {
    shape: 'Circle',
    fill: '#40565A',
    stroke: 'transparent',
    strokeWidth: 1,
    size: new go.Size(24, 24),
    // Play triangle icon (filled)
    icon: {
      fill: '#EBEFF1',
      stroke: 'transparent',
      size: new go.Size(8, 8),
    },
  },
  turn: {
    shape: 'Circle',
    fill: 'transparent',
    stroke: '#EBEFF1',
    strokeWidth: 6,
    size: new go.Size(18, 18),
    size2: new go.Size(21, 21), // this is when the node is in collapsed state
    textStroke: '#e5e7eb',
    secondaryTextStroke: '#94a3b8',
    // Double-ring design
    outerRing: {
      size: new go.Size(32, 32),
      fill: 'transparent',
      stroke: '#40565A',
      strokeWidth: 1,
    },
    // Pulsing pagination loader (visible when this turn is awaiting next-page fetch).
    // PULSE_RING starts small and scales up to roughly OUTER_RING.size at peak.
    pulseRing: {
      size: new go.Size(12, 12),
      fill: 'transparent',
      stroke: 'rgba(255, 255, 255, 0.48)',
      strokeWidth: 1,
      maxScale: 32 / 12, // peak radius matches OUTER_RING
    },
    pulseDot: {
      size: new go.Size(6, 6),
      fill: '#40565A',
      stroke: 'transparent',
      strokeWidth: 0,
    },
    selected: {
      stroke: '#34BCAF',
      strokeWidth: 3,
      shadowColor: '#34BCAF',
      shadowBlur: 6,
      shadowOffset: { x: 0, y: 0 },
    },
    hover: {
      stroke: '#34BCAF',
      strokeWidth: 2,
      shadowColor: 'rgba(255,255,255,0.64)',
      shadowBlur: 8,
      shadowOffset: { x: 0, y: 0 },
    },
  },
  end: {
    shape: 'Circle',
    fill: '#40565A',
    stroke: 'transparent',
    strokeWidth: 1,
    size: new go.Size(24, 24),
    textStroke: '#e5e7eb',
    // Stop square icon (filled)
    icon: {
      fill: '#D9D9D9',
      stroke: 'transparent',
      size: new go.Size(8, 8),
    },
  },
  agent: {
    shape: 'Circle',
    fill: 'white',
    stroke: 'white',
    strokeWidth: 1.5,
    textStroke: '#e5e7eb',
    size: new go.Size(48, 48),
    selected: {
      stroke: '#34BCAF',
      strokeWidth: 3,
      shadowColor: '#34BCAF',
      shadowBlur: 6,
      shadowOffset: { x: 0, y: 0 },
    },
    hover: {
      stroke: '#34BCAF',
      strokeWidth: 2,
      shadowColor: 'rgba(255,255,255,0.64)',
      shadowBlur: 8,
      shadowOffset: { x: 0, y: 0 },
    },
  },
  tool: {
    shape: 'Circle',
    fill: 'white',
    stroke: 'white',
    strokeWidth: 1.5,
    textStroke: '#e5e7eb',
    size: new go.Size(48, 48),
    selected: {
      stroke: '#34BCAF',
      strokeWidth: 3,
      shadowColor: '#34BCAF',
      shadowBlur: 6,
      shadowOffset: { x: 0, y: 0 },
    },
    hover: {
      stroke: '#34BCAF',
      strokeWidth: 2,
      shadowColor: 'rgba(255,255,255,0.64)',
      shadowBlur: 8,
      shadowOffset: { x: 0, y: 0 },
    },
  },
} as const;

// Shared node styles (common across all node categories)
export const NODE_COMMON_STYLES = {
  iconSize: new go.Size(32, 32),
  labelFont: '12px sans-serif',
  labelMargin: new go.Margin(6, 0, 0, 0),
  error: {
    stroke: '#F26C74',
    strokeWidth: 2,
    badgeSize: new go.Size(16, 16),
    badgeAlignment: new go.Spot(0.15, 0.15),
  },
} as const;

// Link style configuration
export const LINK_STYLES = {
  line: {
    strokeWidth: 2,
    stroke: colors.layout['line-secondary'],
  },
  arrow: {
    toArrow: 'Standard',
    fill: colors.layout['line-secondary'],
    stroke: colors.layout['line-secondary'],
  },
  label: {
    background: {
      fill: 'rgba(2,6,23,0.6)',
      stroke: '#334155',
      strokeWidth: 1,
    },
    text: {
      font: '10px sans-serif',
      stroke: '#cbd5e1',
      margin: new go.Margin(1, 4),
    },
  },
  error: {
    stroke: '#E94A53',
    strokeWidth: 2,
    highlightStroke: '#F26C74'
  },
  // Selected/active state
  selected: {
    stroke: '#34BCAF',
    stepCircleFill: '#178A83',
    stepCircleStroke: '#178A83',
    stepTextStroke: 'white',
  },
  // Step number badge on the link
  stepNumber: {
    minSize: new go.Size(18, 18),
    circleFill: '#08242F',
    circleStroke: colors.layout['line-secondary'],
    circleStrokeWidth: 1,
    textFont: '12px sans-serif',
    textStroke: 'white',
  },
  // Tool label that appears mid-link
  edgeLabel: {
    backgroundFill: '#08242F',
    textFont: '12px sans-serif',
    textStroke: '#94a3b8',
    textMargin: new go.Margin(0, 8),
  },
  // Highlight styles for path tracing
  highlight: {
    strokeWidth: 2,
    stroke: '#8C9EA1',
    stepCircleFill: '#8C9EA1',
    stepCircleStroke: '#8C9EA1',
    stepTextStroke: '#061419',
  },
  // Dimmed styles for non-highlighted edges
  dimmed: {
    strokeWidth: 1,
    stroke: '#475569',
    opacity: 0.4,
  },
} as const;

// Shared tooltip styles (used across node and link tooltips)
export const TOOLTIP_STYLES = {
  minWidth: 218,
  background: {
    fill: '#08242F',
    stroke: colors.layout['line-secondary'],
  },
  error: {
    fill: '#301617',
    font: '400 14px sans-serif',
    stroke: colors.notification['error-dark'],
    dividerStroke:'rgba(140, 158, 161, 0.20)'
  },
  // General node tooltip text (turn-node, link subtitle)
  text: {
    margin: new go.Margin(6, 10),
    font: '12px sans-serif',
    stroke: '#e5e7eb',
    maxSize: new go.Size(400, NaN),
  },
  // Agent/tool tooltip text (smaller max width)
  nodeText: {
    margin: new go.Margin(16, 16),
    font: '12px sans-serif',
    stroke: colors.text.subtle,
    maxSize: new go.Size(300, NaN),
  },
  // Link tooltip panel and text styles
  link: {
    panelMargin: new go.Margin(16),
    errorPanelMargin: new go.Margin(8, 12, 12, 12),
    title: {
      font: '500 16px sans-serif',
      stroke: '#FFFFFF',
      margin: new go.Margin(0, 0, 8, 0),
    },
    subtitle: {
      font: '11px sans-serif',
      stroke: colors.text.subtle,
      maxSize: new go.Size(400, NaN),
    },
  },
} as const;

// Skeleton/ghost placeholder node styles
export const SKELETON_STYLES = {
  circle: {
    size: new go.Size(48, 48),
    fill: 'rgba(140, 158, 161, 0.12)',
    stroke: 'rgba(140, 158, 161, 0.25)',
    strokeWidth: 1.5,
  },
  innerCircle: {
    size: new go.Size(20, 20),
    fill: 'rgba(140, 158, 161, 0.1)',
    stroke: 'rgba(140, 158, 161, 0.15)',
    strokeWidth: 1,
  },
  labelBar: {
    size: new go.Size(180, 10),
    fill: 'rgba(140, 158, 161, 0.12)',
    stroke: 'transparent',
    margin: new go.Margin(8, 0, 0, 0),
    cornerRadius: 3,
  },
} as const;

// Layout configurations
export const LAYOUT_CONFIG = {
  layeredDigraph: {
    direction: 0,
    layerSpacing: 200,
    columnSpacing: 60,
  },
} as const;

// Edge label styles for vertical turn-chain edges
export const TURN_EDGE_LABEL_STYLES = {
  text: {
    font: '11px sans-serif',
    stroke: '#8C9EA1',
  },
  // Offset the label to the right of the vertical line
  segmentOffset: new go.Point(16, 0),
} as const;

// Expander button styles
export const EXPANDER_STYLES = {
  size: 16,
  fill: '#FFFFFF',
  stroke: '#257C9D',
  strokeWidth: 1,
  iconStroke: '#257C9D',
  iconStrokeWidth: 1.5,
} as const;

// Node categories for template mapping
export type NodeCategory = 'start' | 'turn' | 'end' | 'agent' | 'tool';
